import React, { useState, useRef, useEffect } from 'react';
import { 
  Megaphone, 
  Plus, 
  Trash2, 
  Edit3, 
  ArrowUp, 
  ArrowDown, 
  Eye, 
  Check, 
  Sparkles, 
  ExternalLink,
  Layers,
  Calendar,
  MousePointerClick,
  SlidersHorizontal,
  Smartphone,
  Monitor,
  Upload,
  Image as ImageIcon,
  Palette,
  ArrowUpToLine,
  ArrowDownToLine
} from 'lucide-react';
import { Modal } from '../common/Modal';
import { AdminApiClient } from '../../services/adminApiClient';

export interface AdBanner {
  id: string;
  title: string;
  subtitle: string;
  ctaText: string;
  targetUrl: string;
  imageUrl: string;
  bgMode?: 'image' | 'color';
  bgColor?: string;
  textColor?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  placement: 'web_hero' | 'mobile_carousel' | 'category_strip' | 'sidebar_deal';
  priority: number;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  clicksCount: number;
  impressionsCount: number;
}

const PLACEMENTS = [
  { id: 'all', label: 'All Placements' },
  { id: 'web_hero', label: 'Web Hero Slider', icon: Monitor },
  { id: 'mobile_carousel', label: 'Mobile Carousel', icon: Smartphone },
  { id: 'category_strip', label: 'Category Strip', icon: Layers },
  { id: 'sidebar_deal', label: 'Sidebar Deal', icon: SlidersHorizontal },
];

const PRESET_BANNER_COLORS = [
  { label: 'Emerald Deep', bg: '#10231D', text: '#FFFFFF', btn: '#0DF2A6', btnText: '#0A1512' },
  { label: 'Emerald Gradient', bg: 'linear-gradient(135deg, #10231D 0%, #1A4736 100%)', text: '#FFFFFF', btn: '#0DF2A6', btnText: '#0A1512' },
  { label: 'Mint White Pastel', bg: '#E8F7EE', text: '#0D1E18', btn: '#10231D', btnText: '#FFFFFF' },
  { label: 'Teal Blue Ocean', bg: 'linear-gradient(135deg, #0D9488 0%, #0369A1 100%)', text: '#FFFFFF', btn: '#FEF08A', btnText: '#713F12' },
  { label: 'Sunset Amber Glow', bg: 'linear-gradient(135deg, #B45309 0%, #EA580C 100%)', text: '#FFFFFF', btn: '#FFFFFF', btnText: '#7C2D12' },
  { label: 'Midnight Obsidian', bg: '#0A1210', text: '#F0FDF4', btn: '#22C55E', btnText: '#052E16' },
];

export const BannerCarouselManager: React.FC = () => {
  const [placementFilter, setPlacementFilter] = useState<string>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [banners, setBanners] = useState<AdBanner[]>([
    {
      id: 'ban_001',
      title: 'Swap Gadgets with 100% Escrow Protection',
      subtitle: 'Over 50,000 verified pre-loved phones, laptops & appliances with 48H inspection guarantee.',
      ctaText: 'Explore Electronics',
      targetUrl: '/market?category=Electronics',
      imageUrl: 'https://images.unsplash.com/photo-1550009158-9ebf69173e03?w=1200&auto=format&fit=crop&q=80',
      bgMode: 'image',
      bgColor: '#10231D',
      textColor: '#FFFFFF',
      buttonColor: '#0DF2A6',
      buttonTextColor: '#0A1512',
      placement: 'web_hero',
      priority: 1,
      isActive: true,
      startDate: '2026-09-01',
      endDate: '2026-10-31',
      clicksCount: 1420,
      impressionsCount: 28900,
    },
    {
      id: 'ban_002',
      title: 'Sell Your Pre-Loved Goods • Instant Bank Payout',
      subtitle: 'Zero listing fee for your first 10 items. Escrow release directly to Nigerian bank accounts.',
      ctaText: 'Start Selling Today',
      targetUrl: '/sell',
      imageUrl: 'https://images.unsplash.com/photo-1556742049-0a67c5574f73?w=800&auto=format&fit=crop&q=80',
      bgMode: 'image',
      bgColor: '#152A23',
      textColor: '#FFFFFF',
      buttonColor: '#0DF2A6',
      buttonTextColor: '#0A1512',
      placement: 'mobile_carousel',
      priority: 2,
      isActive: true,
      startDate: '2026-09-10',
      endDate: '2026-12-31',
      clicksCount: 980,
      impressionsCount: 18400,
    },
    {
      id: 'ban_003',
      title: 'Clean Energy Fair: Solar Panels & Inverters',
      subtitle: 'Slash your generator fuel bills. Buy vetted solar equipment with certified battery health.',
      ctaText: 'View Solar Deals',
      targetUrl: '/market?category=Solar',
      imageUrl: '',
      bgMode: 'color',
      bgColor: 'linear-gradient(135deg, #0F766E 0%, #154A3A 100%)',
      textColor: '#FFFFFF',
      buttonColor: '#FEF08A',
      buttonTextColor: '#713F12',
      placement: 'category_strip',
      priority: 3,
      isActive: true,
      startDate: '2026-09-15',
      endDate: '2026-11-15',
      clicksCount: 640,
      impressionsCount: 9200,
    },
    {
      id: 'ban_004',
      title: 'Tier 2 Verified Merchant Upgrade Bonus',
      subtitle: 'Upload CAC or Government ID to unlock unlimited trades & silver trust badge.',
      ctaText: 'Complete KYC',
      targetUrl: '/kyc',
      imageUrl: '',
      bgMode: 'color',
      bgColor: '#10231D',
      textColor: '#FFFFFF',
      buttonColor: '#0DF2A6',
      buttonTextColor: '#0A1512',
      placement: 'sidebar_deal',
      priority: 4,
      isActive: false,
      startDate: '2026-09-01',
      endDate: '2026-09-30',
      clicksCount: 310,
      impressionsCount: 5400,
    },
  ]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<AdBanner | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formSubtitle, setFormSubtitle] = useState('');
  const [formCta, setFormCta] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [formImage, setFormImage] = useState('');
  const [formBgMode, setFormBgMode] = useState<'image' | 'color'>('image');
  const [formBgColor, setFormBgColor] = useState('#10231D');
  const [formTextColor, setFormTextColor] = useState('#FFFFFF');
  const [formButtonColor, setFormButtonColor] = useState('#0DF2A6');
  const [formButtonTextColor, setFormButtonTextColor] = useState('#0A1512');
  const [formPlacement, setFormPlacement] = useState<AdBanner['placement']>('web_hero');
  const [formIsActive, setFormIsActive] = useState(true);

  const handleOpenAdd = () => {
    setEditingBanner(null);
    setFormTitle('');
    setFormSubtitle('');
    setFormCta('Explore Deals');
    setFormUrl('/market');
    setFormImage('https://images.unsplash.com/photo-1550009158-9ebf69173e03?w=1200&auto=format&fit=crop&q=80');
    setFormBgMode('image');
    setFormBgColor('#10231D');
    setFormTextColor('#FFFFFF');
    setFormButtonColor('#0DF2A6');
    setFormButtonTextColor('#0A1512');
    setFormPlacement('web_hero');
    setFormIsActive(true);
    setModalOpen(true);
  };

  const handleOpenEdit = (b: AdBanner) => {
    setEditingBanner(b);
    setFormTitle(b.title);
    setFormSubtitle(b.subtitle);
    setFormCta(b.ctaText);
    setFormUrl(b.targetUrl);
    setFormImage(b.imageUrl || '');
    setFormBgMode(b.bgMode || (b.imageUrl ? 'image' : 'color'));
    setFormBgColor(b.bgColor || '#10231D');
    setFormTextColor(b.textColor || '#FFFFFF');
    setFormButtonColor(b.buttonColor || '#0DF2A6');
    setFormButtonTextColor(b.buttonTextColor || '#0A1512');
    setFormPlacement(b.placement);
    setFormIsActive(b.isActive);
    setModalOpen(true);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setFormImage(reader.result);
        setFormBgMode('image');
      }
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    AdminApiClient.getAllBanners()
      .then(data => {
        if (data && data.length > 0) setBanners(data);
      })
      .catch(err => console.warn('Could not load banners from PostgreSQL API:', err.message));
  }, []);

  const handleSave = () => {
    if (!formTitle.trim()) return;

    const payload: AdBanner = {
      id: editingBanner ? editingBanner.id : `ban_${Date.now()}`,
      title: formTitle,
      subtitle: formSubtitle,
      ctaText: formCta,
      targetUrl: formUrl,
      imageUrl: formBgMode === 'image' ? formImage : '',
      bgMode: formBgMode,
      bgColor: formBgColor,
      textColor: formTextColor,
      buttonColor: formButtonColor,
      buttonTextColor: formButtonTextColor,
      placement: formPlacement,
      priority: editingBanner ? editingBanner.priority : banners.length + 1,
      isActive: formIsActive,
      clicksCount: editingBanner ? editingBanner.clicksCount : 0,
      impressionsCount: editingBanner ? editingBanner.impressionsCount : 0,
    };

    // Optimistic UI update
    if (editingBanner) {
      setBanners(prev => prev.map(b => b.id === editingBanner.id ? payload : b));
    } else {
      setBanners(prev => [...prev, payload]);
    }

    // Live Backend Persistence + Instant WebSocket Broadcast to Flutter App
    AdminApiClient.upsertBanner(payload)
      .then(res => {
        if (res?.banner) {
          setBanners(prev => prev.map(b => (b.id === editingBanner?.id || b.title === res.banner.title) ? res.banner : b));
        }
      })
      .catch(err => console.error('Save banner error:', err.message));

    setModalOpen(false);
  };

  const handleToggleActive = (id: string) => {
    const banner = banners.find(b => b.id === id);
    if (!banner) return;
    const newActive = !banner.isActive;
    setBanners(prev => prev.map(b => b.id === id ? { ...b, isActive: newActive } : b));
    AdminApiClient.upsertBanner({ ...banner, isActive: newActive }).catch(err => console.warn(err.message));
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this promotional ad banner?')) {
      setBanners(prev => {
        const remaining = prev.filter(b => b.id !== id);
        return remaining.map((b, idx) => ({ ...b, priority: idx + 1 }));
      });
      AdminApiClient.deleteBanner(id).catch(err => console.warn(err.message));
    }
  };

  const handleMove = (index: number, direction: 'up' | 'down' | 'top' | 'bottom') => {
    const updated = [...banners];
    const item = updated.splice(index, 1)[0];

    if (direction === 'top') {
      updated.unshift(item);
    } else if (direction === 'bottom') {
      updated.push(item);
    } else if (direction === 'up' && index > 0) {
      updated.splice(index - 1, 0, item);
    } else if (direction === 'down' && index < banners.length - 1) {
      updated.splice(index + 1, 0, item);
    } else {
      updated.splice(index, 0, item);
    }

    setBanners(updated.map((b, i) => ({ ...b, priority: i + 1 })));
  };

  const filteredBanners = placementFilter === 'all'
    ? banners
    : banners.filter(b => b.placement === placementFilter);

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border bg-[#152a23] dark:bg-[#152a23] border-[#22493c] light:bg-white light:border-[#D5E4DC]">
        <div>
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-brand-400" />
            <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
              Ad Banners & Hero Carousels Manager
            </h4>
          </div>
          <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-1">
            Upload custom banner graphics from your computer or design colored background banners. Reorder priority, customize CTA button styling, and manage live placements.
          </p>
        </div>

        <button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>Create New Ad Banner</span>
        </button>
      </div>

      {/* Placement Filter Pills */}
      <div className="flex flex-wrap items-center gap-2">
        {PLACEMENTS.map((p) => {
          const isSelected = placementFilter === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setPlacementFilter(p.id)}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-2 ${
                isSelected
                  ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                  : 'bg-[#152a23] text-[#90cbb7] hover:text-white border border-[#22493c]'
              }`}
            >
              {p.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-forest-void/20 text-forest-void font-bold' : 'bg-[#10231d] text-[#90cbb7]'}`}>
                {p.id === 'all' ? banners.length : banners.filter(b => b.placement === p.id).length}
              </span>
            </button>
          );
        })}
      </div>

      {/* Grid of Banners */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {filteredBanners.map((banner) => {
          const originalIdx = banners.findIndex(b => b.id === banner.id);
          const ctr = banner.impressionsCount > 0
            ? ((banner.clicksCount / banner.impressionsCount) * 100).toFixed(1)
            : '0.0';

          const isColorBg = banner.bgMode === 'color' || !banner.imageUrl;

          return (
            <div
              key={banner.id}
              className={`rounded-2xl border overflow-hidden transition-all duration-300 flex flex-col justify-between ${
                banner.isActive
                  ? 'bg-[#152a23] border-[#22493c] hover:border-brand-500/40 shadow-sm'
                  : 'bg-[#12221c]/70 border-[#1c392f] opacity-80'
              }`}
            >
              <div>
                {/* Visual Banner Preview Container */}
                <div
                  className="h-44 w-full relative p-5 flex flex-col justify-between overflow-hidden"
                  style={
                    isColorBg
                      ? { background: banner.bgColor || '#10231D', color: banner.textColor || '#FFFFFF' }
                      : { backgroundImage: `url(${banner.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                  }
                >
                  {/* Subtle dark overlay for image mode to ensure text readability */}
                  {!isColorBg && (
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/30 pointer-events-none" />
                  )}

                  {/* Top Badges */}
                  <div className="relative z-10 flex items-center justify-between">
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider bg-black/50 text-brand-300 backdrop-blur-md border border-white/15">
                      {banner.placement.replace('_', ' ')}
                    </span>

                    <button
                      onClick={() => handleToggleActive(banner.id)}
                      className={`text-[10px] font-bold px-2.5 py-1 rounded-full transition-all border ${
                        banner.isActive
                          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                          : 'bg-slate-500/20 text-slate-300 border-slate-500/30'
                      }`}
                    >
                      {banner.isActive ? 'Active (Live)' : 'Paused'}
                    </button>
                  </div>

                  {/* Banner Texts & CTA Preview */}
                  <div className="relative z-10 flex items-end justify-between gap-3">
                    <div className="max-w-[70%]">
                      <h4 className="font-extrabold text-base line-clamp-1 drop-shadow-sm" style={{ color: banner.textColor || '#FFFFFF' }}>
                        {banner.title}
                      </h4>
                      <p className="text-xs line-clamp-2 mt-0.5 opacity-90" style={{ color: banner.textColor || '#FFFFFF' }}>
                        {banner.subtitle}
                      </p>
                    </div>

                    <button
                      className="px-3 py-1.5 rounded-full text-xs font-bold shadow-md shrink-0 transition-transform active:scale-95"
                      style={{ 
                        backgroundColor: banner.buttonColor || '#0DF2A6', 
                        color: banner.buttonTextColor || '#0A1512' 
                      }}
                    >
                      {banner.ctaText}
                    </button>
                  </div>
                </div>

                {/* Banner Metadata & Performance */}
                <div className="p-5 space-y-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-[#90cbb7]">Target URL / Link:</span>
                    <a
                      href={banner.targetUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-400 font-mono hover:underline flex items-center gap-1 max-w-[200px] truncate"
                    >
                      <span className="truncate">{banner.targetUrl}</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-[#90cbb7]">Background Styling:</span>
                    <div className="flex items-center gap-1.5">
                      <div 
                        className="w-4 h-4 rounded-full border border-white/20" 
                        style={isColorBg ? { background: banner.bgColor } : { backgroundImage: `url(${banner.imageUrl})`, backgroundSize: 'cover' }} 
                      />
                      <span className="font-semibold text-white capitalize">{banner.bgMode || 'Image'}</span>
                    </div>
                  </div>

                  {/* Performance CTR bar */}
                  <div className="p-3 rounded-xl bg-[#10231d] border border-[#22493c] grid grid-cols-3 gap-2 text-center">
                    <div>
                      <span className="text-[10px] text-[#90cbb7] block">Impressions</span>
                      <span className="font-bold text-white mt-0.5 block">{banner.impressionsCount.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-[#90cbb7] block">Clicks</span>
                      <span className="font-bold text-brand-400 mt-0.5 block">{banner.clicksCount.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-[#90cbb7] block">CTR</span>
                      <span className="font-bold text-amber-400 mt-0.5 block">{ctr}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions & Priority Reordering Footer */}
              <div className="p-4 bg-[#10231d]/60 border-t border-[#22493c] flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleMove(originalIdx, 'top')}
                    disabled={originalIdx === 0}
                    className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] disabled:opacity-25"
                    title="Send to Very Top"
                  >
                    <ArrowUpToLine className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleMove(originalIdx, 'up')}
                    disabled={originalIdx === 0}
                    className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] disabled:opacity-25"
                    title="Move Up 1 Position"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleMove(originalIdx, 'down')}
                    disabled={originalIdx === banners.length - 1}
                    className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] disabled:opacity-25"
                    title="Move Down 1 Position"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleMove(originalIdx, 'bottom')}
                    disabled={originalIdx === banners.length - 1}
                    className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] disabled:opacity-25"
                    title="Send to Very Bottom"
                  >
                    <ArrowDownToLine className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-[11px] text-[#90cbb7] ml-2 font-mono">Priority #{banner.priority}</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenEdit(banner)}
                    className="p-1.5 rounded-lg text-brand-400 hover:bg-brand-500/15"
                    title="Edit Banner Visuals & Details"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(banner.id)}
                    className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-500/15"
                    title="Delete Banner"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create / Edit Modal with File Upload & Color Customizers */}
      {modalOpen && (
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editingBanner ? 'Edit Ad Banner & Visual Styling' : 'Create New Ad Banner'}
          subtitle="Configure headline, description, background image or colored canvas, and CTA button"
          maxWidth="xl"
        >
          <div className="space-y-4 text-xs">
            {/* Live Interactive Modal Preview */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-[#90cbb7] mb-1">
                Live Preview
              </label>
              <div 
                className="h-36 w-full rounded-2xl p-4 flex flex-col justify-between border border-[#22493c] relative overflow-hidden shadow-inner"
                style={
                  formBgMode === 'color' || !formImage
                    ? { background: formBgColor, color: formTextColor }
                    : { backgroundImage: `url(${formImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                }
              >
                {formBgMode === 'image' && formImage && (
                  <div className="absolute inset-0 bg-black/50 pointer-events-none" />
                )}
                <div className="relative z-10 flex items-center justify-between">
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-black/40 text-brand-300 backdrop-blur-md">
                    {formPlacement.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
                    PREVIEW
                  </span>
                </div>
                <div className="relative z-10 flex items-end justify-between gap-3">
                  <div className="max-w-[70%]">
                    <h5 className="font-extrabold text-sm line-clamp-1" style={{ color: formTextColor }}>
                      {formTitle || 'Sample Banner Title'}
                    </h5>
                    <p className="text-[11px] line-clamp-1 opacity-90" style={{ color: formTextColor }}>
                      {formSubtitle || 'Sample banner description and subtitle copy'}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="px-3 py-1 rounded-full text-xs font-bold shadow-md shrink-0"
                    style={{ backgroundColor: formButtonColor, color: formButtonTextColor }}
                  >
                    {formCta || 'Click Here'}
                  </button>
                </div>
              </div>
            </div>

            {/* Headline and Description */}
            <div>
              <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                Banner Headline / Title:
              </label>
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Flash Thrift Deals: 50% Off Clean Inverters"
                className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                Subtitle / Description:
              </label>
              <textarea
                value={formSubtitle}
                onChange={(e) => setFormSubtitle(e.target.value)}
                rows={2}
                placeholder="e.g. Verified by certified technicians with 48-hour escrow protection."
                className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
              />
            </div>

            {/* Background Mode Toggle: Image vs Colored Background */}
            <div className="p-3.5 rounded-2xl bg-[#10231D] border border-[#22493c] space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-white flex items-center gap-1.5">
                  <Palette className="w-4 h-4 text-brand-400" />
                  Background Style
                </span>
                <div className="flex items-center gap-1 bg-[#0c1c17] p-0.5 rounded-lg border border-[#22493c]">
                  <button
                    type="button"
                    onClick={() => setFormBgMode('image')}
                    className={`px-3 py-1 rounded text-xs font-bold flex items-center gap-1.5 ${
                      formBgMode === 'image' ? 'bg-brand-500 text-forest-void' : 'text-[#90cbb7]'
                    }`}
                  >
                    <ImageIcon className="w-3.5 h-3.5" />
                    <span>Image</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormBgMode('color')}
                    className={`px-3 py-1 rounded text-xs font-bold flex items-center gap-1.5 ${
                      formBgMode === 'color' ? 'bg-brand-500 text-forest-void' : 'text-[#90cbb7]'
                    }`}
                  >
                    <Palette className="w-3.5 h-3.5" />
                    <span>Colored / Gradient</span>
                  </button>
                </div>
              </div>

              {formBgMode === 'image' ? (
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={formImage}
                      onChange={(e) => setFormImage(e.target.value)}
                      placeholder="Image URL (https://...) or upload file from device"
                      className="flex-1 p-2 rounded-xl text-xs bg-[#152a23] border border-[#22493c] text-white font-mono"
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3.5 py-2 rounded-xl text-xs font-bold bg-[#22493c] hover:bg-[#2c5c4d] text-white border border-brand-500/30 flex items-center gap-1.5 shrink-0"
                    >
                      <Upload className="w-3.5 h-3.5 text-brand-400" />
                      <span>Upload from Device</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <span className="text-[10px] text-[#90cbb7] block mb-1">Canvas Background</span>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="color"
                          value={formBgColor.startsWith('#') ? formBgColor : '#10231D'}
                          onChange={(e) => setFormBgColor(e.target.value)}
                          className="w-8 h-8 rounded border border-[#22493c] bg-transparent cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formBgColor}
                          onChange={(e) => setFormBgColor(e.target.value)}
                          className="flex-1 px-2 py-1 rounded-lg bg-[#152a23] border border-[#22493c] text-white text-xs font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <span className="text-[10px] text-[#90cbb7] block mb-1">Headline & Text Color</span>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="color"
                          value={formTextColor}
                          onChange={(e) => setFormTextColor(e.target.value)}
                          className="w-8 h-8 rounded border border-[#22493c] bg-transparent cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formTextColor}
                          onChange={(e) => setFormTextColor(e.target.value)}
                          className="flex-1 px-2 py-1 rounded-lg bg-[#152a23] border border-[#22493c] text-white text-xs font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <span className="text-[10px] text-[#90cbb7] block mb-1">CTA Button Fill</span>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="color"
                          value={formButtonColor}
                          onChange={(e) => setFormButtonColor(e.target.value)}
                          className="w-8 h-8 rounded border border-[#22493c] bg-transparent cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formButtonColor}
                          onChange={(e) => setFormButtonColor(e.target.value)}
                          className="flex-1 px-2 py-1 rounded-lg bg-[#152a23] border border-[#22493c] text-white text-xs font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preset Swatches */}
                  <div>
                    <span className="text-[10px] text-[#90cbb7] block mb-1.5">Preset Theme Palettes:</span>
                    <div className="flex flex-wrap gap-2">
                      {PRESET_BANNER_COLORS.map((preset, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setFormBgColor(preset.bg);
                            setFormTextColor(preset.text);
                            setFormButtonColor(preset.btn);
                            setFormButtonTextColor(preset.btnText);
                          }}
                          className="px-2.5 py-1 rounded-lg border border-[#22493c] hover:border-brand-500/40 text-[10px] font-semibold text-white shadow-sm flex items-center gap-1.5"
                          style={{ background: preset.bg }}
                        >
                          <span className="w-2 h-2 rounded-full" style={{ background: preset.btn }} />
                          <span className="bg-black/50 px-1 rounded text-[9px]">{preset.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CTA and Deeplink Configuration */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                  CTA Button Label:
                </label>
                <input
                  type="text"
                  value={formCta}
                  onChange={(e) => setFormCta(e.target.value)}
                  placeholder="e.g. Shop Now"
                  className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                  Button Text Color:
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="color"
                    value={formButtonTextColor}
                    onChange={(e) => setFormButtonTextColor(e.target.value)}
                    className="w-8 h-8 rounded border border-[#22493c] bg-transparent cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formButtonTextColor}
                    onChange={(e) => setFormButtonTextColor(e.target.value)}
                    className="flex-1 px-2 py-2 rounded-xl bg-[#10231d] border border-[#22493c] text-white text-xs font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                  Target Deeplink / URL:
                </label>
                <input
                  type="text"
                  value={formUrl}
                  onChange={(e) => setFormUrl(e.target.value)}
                  placeholder="e.g. /market?category=Solar"
                  className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500 font-mono"
                />
              </div>
            </div>

            {/* Target Placement */}
            <div>
              <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
                Target Placement Channel:
              </label>
              <select
                value={formPlacement}
                onChange={(e) => setFormPlacement(e.target.value as any)}
                className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
              >
                <option value="web_hero">Web Hero Slider (Desktop Mega View)</option>
                <option value="mobile_carousel">Mobile App Home Carousel</option>
                <option value="category_strip">Category Strip Promo Ribbon</option>
                <option value="sidebar_deal">Sidebar Deal Spotlight</option>
              </select>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <input
                type="checkbox"
                id="bannerActive"
                checked={formIsActive}
                onChange={(e) => setFormIsActive(e.target.checked)}
                className="w-4 h-4 rounded text-brand-500 accent-brand-500"
              />
              <label htmlFor="bannerActive" className="text-xs font-semibold text-white cursor-pointer">
                Publish Immediately (Set status to Live on marketplace)
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#22493c]">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!formTitle.trim()}
                className="px-5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 disabled:opacity-50 shadow-glow-sm"
              >
                Save Ad Banner
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
